import { useEffect, useRef, useState } from 'react';
import { MoreVertical } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { ActionMenuItem } from './ActionMenu';
import { ActionMenuIcon } from './ActionMenuIcon';

interface ActionMenuDesktopProps {
  items: ActionMenuItem[];
  align?: 'left' | 'right';
  className?: string;
  buttonClassName?: string;
  label?: string;
}

export function ActionMenuDesktop({ items, align = 'right', className, buttonClassName, label = 'Tùy chọn' }: ActionMenuDesktopProps) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={menuRef} className={cn('relative inline-flex', className)}>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setOpen((value) => !value);
        }}
        className={cn('rounded-md p-1 text-slate-500 hover:bg-slate-100', buttonClassName)}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {open ? (
        <div
          role="menu"
          className={cn(
            'absolute top-full z-50 mt-2 min-w-48 rounded-md border border-slate-200 bg-white p-2 shadow-sm',
            align === 'right' ? 'right-0' : 'left-0',
          )}
        >
          {items.map((item) => {
            return (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={(event) => {
                  event.stopPropagation();
                  if (item.disabled) return;
                  item.onClick?.();
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left sm:text-sm text-xs text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50',
                  item.destructive && 'text-red-600 hover:bg-red-50',
                )}
              >
                {item.icon ? <ActionMenuIcon icon={item.icon} className="h-4 w-4 shrink-0" /> : null}
                <span className="whitespace-nowrap">{item.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
